import { Component } from '@theme/component';

const LEAVE_DURATION = 450;

/**
 * Rotates the top bar messages one at a time. Pauses while the pointer is over the bar or focus is
 * inside it, and while the tab is hidden, so shoppers can read and use a message.
 *
 * @typedef {object} TopbarRefs
 * @property {HTMLElement[]} [messages]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<TopbarRefs>}
 */
class TphTopbarComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | undefined} */
  #timer;
  /** @type {number | undefined} */
  #leaveTimer;
  #index = 0;
  #hovered = false;
  #focused = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('pointerenter', () => this.#setPaused('hovered', true), { signal });
    this.addEventListener('pointerleave', () => this.#setPaused('hovered', false), { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#schedule, { signal });

    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    clearTimeout(this.#timer);
    clearTimeout(this.#leaveTimer);
  }

  get #messages() {
    return this.refs.messages ?? [];
  }

  get #interval() {
    return Math.max(Number(this.dataset.interval) || 4000, 2000);
  }

  /** Shows the previous message. Wired to the left arrow with on:click="/previous". */
  previous() {
    this.#show(-1);
  }

  /** Shows the next message. Wired to the right arrow with on:click="/next". */
  next() {
    this.#show(1);
  }

  #reset() {
    // The arrows only work with script, so they stay hidden until it has run
    if (this.refs.previous) this.refs.previous.hidden = false;
    if (this.refs.next) this.refs.next.hidden = false;
    this.#index = 0;
    this.#messages.forEach((message, i) => {
      message.classList.toggle('is-active', i === 0);
      message.classList.remove('is-leaving');
    });
    this.#schedule();
  }

  /**
   * @param {'hovered' | 'focused'} reason
   * @param {boolean} value
   */
  #setPaused(reason, value) {
    if (reason === 'hovered') this.#hovered = value;
    else this.#focused = value;
    this.#schedule();
  }

  /**
   * Only keyboard focus pauses; a mouse click leaves focus on a button and must not stop autoplay.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    this.#setPaused('focused', target instanceof Element && target.matches(':focus-visible'));
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    this.#setPaused('focused', next instanceof Element && this.contains(next) && next.matches(':focus-visible'));
  };

  #schedule = () => {
    clearTimeout(this.#timer);
    if (this.#messages.length < 2 || this.#hovered || this.#focused || document.hidden) return;
    this.#timer = window.setTimeout(() => this.#show(1), this.#interval);
  };

  /** @param {number} step 1 for the next message, -1 for the previous one */
  #show(step) {
    const messages = this.#messages;
    if (messages.length < 2) return;
    const current = messages[this.#index];
    this.#index = (this.#index + step + messages.length) % messages.length;
    const next = messages[this.#index];

    if (current) {
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      clearTimeout(this.#leaveTimer);
      this.#leaveTimer = window.setTimeout(() => current.classList.remove('is-leaving'), LEAVE_DURATION);
    }
    next?.classList.remove('is-leaving');
    next?.classList.add('is-active');

    this.#schedule();
  }
}

if (!customElements.get('tph-topbar-component')) {
  customElements.define('tph-topbar-component', TphTopbarComponent);
}
